/**
 * Makes React tolerant of browser translation (Google Translate, etc.).
 *
 * Translation tools rewrite text in place, wrapping text nodes in `<font>`
 * elements and re-parenting nodes outside React's knowledge. React keeps
 * references to the original nodes, so when it later removes or reorders a
 * sibling it calls `parentNode.removeChild` / `insertBefore` with a node that is
 * no longer a direct child of that parent. The DOM spec requires those methods
 * to throw `NotFoundError` in that case, which React does not catch — so the
 * whole app white-screens via the error boundary.
 *
 * These overrides turn that one failure mode into a no-op (with a dev warning):
 * when the target/reference node is not a child of `this` — the exact case the
 * native method would otherwise throw — we skip the operation and return the
 * node React expected. Every other call delegates to the original method
 * unchanged, so correct code is unaffected. React's next full re-render of the
 * affected subtree reconciles any left-over node; the cost is at most brief
 * visual staleness, never a crash.
 *
 * This is the de-facto standard mitigation for React under browser translation
 * (see facebook/react#11538). It is intentionally global — it must also cover
 * DOM mutations made by third-party widgets, not only React.
 */
let installed = false

export function installBrowserTranslationCrashGuard(): void {
  if (installed || typeof Node !== 'function' || !Node.prototype) {
    return
  }
  installed = true

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function removeChild<T extends Node>(
    this: Node,
    child: T,
  ): T {
    if (child.parentNode !== this) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          'browserTranslationCrashGuard: skipped removeChild for a node re-parented outside React (browser translation).',
        )
      }
      return child
    }
    originalRemoveChild.call(this, child)
    return child
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode !== null && referenceNode.parentNode !== this) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          'browserTranslationCrashGuard: skipped insertBefore for a reference node re-parented outside React (browser translation).',
        )
      }
      return newNode
    }
    originalInsertBefore.call(this, newNode, referenceNode)
    return newNode
  }
}
